import React, { useEffect, useRef, useState } from "react";
import "@/components/ThankYou.css";

const CITIES = [
  ["Sofía C.", "Buenos Aires"],
  ["Andrés V.", "Córdoba"],
  ["María José R.", "Rosario"],
  ["Carlos M.", "Mendoza"],
  ["Diego A.", "La Plata"],
  ["Laura P.", "Mar del Plata"],
  ["Fernando G.", "San Miguel de Tucumán"],
  ["Patricia S.", "Salta"],
  ["Roberto L.", "Neuquén"],
  ["Ana M.", "Bahía Blanca"],
  ["José D.", "San Juan"],
  ["Gabriela H.", "Paraná"],
  ["Luis E.", "Corrientes"],
  ["Marcela T.", "Posadas"],
  ["Alejandro B.", "Ushuaia"],
  ["Belén F.", "Río Gallegos"],
];

const initials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export default function ThankYou() {
  const [firstName, setFirstName] = useState("");
  const [timestamp, setTimestamp] = useState("");
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("Procesando su solicitud");
  const [step2Done, setStep2Done] = useState(false);
  const [step3Active, setStep3Active] = useState(false);
  const [feed, setFeed] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [badge, setBadge] = useState(1);
  const chatReadyRef = useRef(false);
  const feedIndexRef = useRef(0);

  // Personalize + timestamp
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get("name") || params.get("firstName") || params.get("nombre") || "";
      if (raw) {
        setFirstName(raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase());
      }
    } catch (_) {}
    const now = new Date();
    const d = String(now.getDate()).padStart(2, "0");
    const mo = MONTHS[now.getMonth()];
    const y = now.getFullYear();
    const h = String(now.getHours()).padStart(2, "0");
    const m = String(now.getMinutes()).padStart(2, "0");
    setTimestamp(`${d} de ${mo} de ${y} • ${h}:${m}`);

    document.title = "Solicitud Confirmada — Flowplanar";
  }, []);

  // Progress animation
  useEffect(() => {
    const t1 = setTimeout(() => setProgress(50), 800);
    const t2 = setTimeout(() => {
      setStep2Done(true);
      setStatusText("Asesor asignado a su cuenta");
    }, 2500);
    const t3 = setTimeout(() => setProgress(100), 4000);
    const t4 = setTimeout(() => {
      setStep3Active(true);
      setStatusText("Conectando con su asesor");
    }, 5000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);

  // Seed feed + schedule
  useEffect(() => {
    const seed = [];
    for (let i = 0; i < 4; i++) {
      const c = CITIES[feedIndexRef.current % CITIES.length];
      feedIndexRef.current++;
      seed.push({
        id: `s-${i}`,
        name: c[0],
        city: c[1],
        mins: Math.floor(Math.random() * 8) + 1,
      });
    }
    setFeed(seed);

    let timer;
    const schedule = () => {
      const delay = 12000 + Math.random() * 10000;
      timer = setTimeout(() => {
        const c = CITIES[feedIndexRef.current % CITIES.length];
        feedIndexRef.current++;
        setFeed((prev) => {
          const next = [
            {
              id: `n-${Date.now()}`,
              name: c[0],
              city: c[1],
              mins: Math.floor(Math.random() * 8) + 1,
            },
            ...prev,
          ];
          return next.slice(0, 5);
        });
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  // Chat auto-messages
  const scheduleMessages = () => {
    if (chatReadyRef.current) return;
    chatReadyRef.current = true;
    const msgs = [
      { text: "¡Hola! Bienvenido a Flowplanar. Su solicitud ha sido recibida y está siendo procesada.", delay: 0 },
      { text: "Su asesor personal ha sido notificado y se está preparando para contactarlo.", delay: 18000 },
      { text: "Recordatorio: recibirá una llamada desde un número registrado en Argentina o desde el exterior. Por favor mantenga su teléfono cerca.", delay: 40000 },
    ];
    msgs.forEach((msg, i) => {
      setTimeout(() => {
        setTyping(true);
        setTimeout(() => {
          setTyping(false);
          const now = new Date();
          setMessages((prev) => [
            ...prev,
            {
              text: msg.text,
              time: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
            },
          ]);
          if (i > 0) {
            setBadge((b) => b + 1);
          }
        }, 1500);
      }, msg.delay);
    });
  };

  useEffect(() => {
    const t = setTimeout(() => scheduleMessages(), 3000);
    return () => clearTimeout(t);
  }, []);

  const toggleChat = () => {
    setChatOpen((v) => {
      const next = !v;
      if (next) {
        setBadge(0);
        if (!chatReadyRef.current) scheduleMessages();
      }
      return next;
    });
  };

  // Count-up simple
  const CountUp = ({ target, suffix = "" }) => {
    const [val, setVal] = useState(0);
    useEffect(() => {
      const duration = 1400;
      const start = performance.now();
      let raf;
      const step = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setVal(Math.floor(ease * target));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
      return () => cancelAnimationFrame(raf);
    }, [target]);
    if (target >= 1000) {
      return <>{val >= 1000 ? Math.floor(val / 1000) + "K" : val}{suffix || "+"}</>;
    }
    return <>{val}{suffix}</>;
  };

  return (
    <div className="ty-root">
      {/* HERO */}
      <section className="ty-hero">
        <div className="ty-hero__content">
          <div className="ty-status">
            <span className="ty-dot" />
            <span>Solicitud activa</span>
          </div>
          <h1>{firstName ? `${firstName}, su registro ha sido confirmado` : "Su registro ha sido confirmado"}</h1>
          <p className="ty-subtitle">Un asesor certificado está siendo asignado a su cuenta</p>
          <p className="ty-timestamp">{timestamp}</p>
        </div>
      </section>

      {/* PROGRESS CARD */}
      <div className="ty-progress">
        <div className="ty-progress__track">
          <div className="ty-progress__lineBg">
            <div className="ty-progress__lineFill" style={{ width: `${progress}%` }} />
          </div>
          <div className="ty-pStep">
            <div className="ty-pDot ty-pDot--done">
              <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <span className="ty-pLabel">Solicitud<br />recibida</span>
          </div>
          <div className="ty-pStep">
            <div className={`ty-pDot ${step2Done ? "ty-pDot--done" : ""}`}>
              <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <span className="ty-pLabel">Asesor<br />asignado</span>
          </div>
          <div className="ty-pStep">
            <div className={`ty-pDot ${step3Active ? "ty-pDot--active" : ""}`}>
              <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <span className={`ty-pLabel ${step3Active ? "ty-pLabel--active" : ""}`}>Conexión</span>
          </div>
        </div>
        <div className="ty-progress__status">
          <p><strong>{statusText}</strong><span className="ty-dots"><i /><i /><i /></span></p>
        </div>
      </div>

      <div className="ty-main">
        {/* CALL ALERT */}
        <div className="ty-call">
          <div className="ty-call__head">
            <div className="ty-call__icon">
              <svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>
            </div>
            <h3>Recibirá una llamada en breve</h3>
          </div>
          <p>Un asesor financiero certificado se comunicará con usted para guiarlo paso a paso en la configuración de su cuenta y responder personalmente todas sus consultas.</p>
          <div className="ty-intl">
            <strong>Acerca de su asesor</strong>
            Debido a la alta demanda de nuevos solicitantes en Argentina, su requerimiento puede ser atendido por uno de nuestros asesores acreditados desde Buenos Aires, Córdoba, Montevideo, Santiago de Chile, Ciudad de México o Madrid. Todos cuentan con certificación en mercados financieros y tecnologías analíticas, y están capacitados para atenderlo en español.
          </div>
        </div>

        {/* LIVE FEED */}
        <div className="ty-feed">
          <div className="ty-feed__head">
            <div className="ty-feed__dot" />
            <span className="ty-feed__title">Registros en tiempo real</span>
          </div>
          <div className="ty-feed__list">
            {feed.map((f) => (
              <div className="ty-feedItem" key={f.id}>
                <div className="ty-feedItem__avatar">{initials(f.name)}</div>
                <div className="ty-feedItem__text">
                  <strong>{f.name}</strong> de {f.city} acaba de registrarse
                </div>
                <div className="ty-feedItem__time">hace {f.mins} min</div>
              </div>
            ))}
          </div>
        </div>

        {/* STEPS */}
        <div className="ty-steps">
          <h2>Lo que sucederá a continuación</h2>

          <div className="ty-stepRow">
            <div className="ty-stepCircle">
              <svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>
            </div>
            <div className="ty-stepBody">
              <h4>Llamada de bienvenida</h4>
              <p>Su asesor personal analizará su perfil, experiencia y objetivos como usuario en Argentina.</p>
            </div>
          </div>

          <div className="ty-stepRow">
            <div className="ty-stepCircle">
              <svg viewBox="0 0 24 24"><path d="M12 15a3 3 0 100-6 3 3 0 000 6z" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>
            </div>
            <div className="ty-stepBody">
              <h4>Configuración de su cuenta</h4>
              <p>Juntos configurarán la plataforma según sus objetivos y nivel de experiencia.</p>
            </div>
          </div>

          <div className="ty-stepRow">
            <div className="ty-stepCircle">
              <svg viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>
            </div>
            <div className="ty-stepBody">
              <h4>Activación de su cuenta analítica</h4>
              <p>Su cuenta será activada y comenzará a acceder a los paneles personalizados en pesos argentinos y dólares.</p>
            </div>
          </div>
        </div>

        {/* TRUST */}
        <div className="ty-trust">
          <div className="ty-trust__head">
            <div className="ty-trust__bank">
              <svg viewBox="0 0 120 40" width="110" height="36" aria-hidden="true">
                <rect x="0" y="0" width="120" height="40" rx="6" fill="#F97066" />
                <text x="60" y="26" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="14" fontWeight="700" fill="#fff">Banco Galicia</text>
              </svg>
            </div>
            <div className="ty-trust__headText">
              <strong>Infraestructura bancaria argentina</strong>
              <span>Procesamiento de operaciones en Argentina</span>
            </div>
          </div>
          <div className="ty-trust__body">
            <p>Sus operaciones son procesadas a través de la infraestructura bancaria argentina, garantizando seguridad y confiabilidad en cada transacción. Su cuenta opera en pesos argentinos y dólares, de acuerdo a la normativa vigente.</p>
            <div className="ty-trust__grid">
              <div className="ty-trust__item">
                <div className="ty-trust__val"><CountUp target={14000} suffix="K+" /></div>
                <div className="ty-trust__lbl">Clientes activos</div>
              </div>
              <div className="ty-trust__item">
                <div className="ty-trust__val"><CountUp target={23} /></div>
                <div className="ty-trust__lbl">Provincias</div>
              </div>
              <div className="ty-trust__item">
                <div className="ty-trust__val">2019</div>
                <div className="ty-trust__lbl">Desde</div>
              </div>
            </div>
          </div>
          <div className="ty-trust__footer">
            <svg className="ty-shield" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            <span>Datos protegidos con encriptación de nivel bancario</span>
          </div>
        </div>

        <div className="ty-disc">
          Los rendimientos pasados no garantizan resultados futuros. Toda operación conlleva riesgos. Consulte a un asesor autorizado antes de operar. Regulado por la CNV — Comisión Nacional de Valores de la República Argentina.
        </div>
      </div>

      {/* CHAT */}
      <button className="ty-chatBtn" onClick={toggleChat} aria-label="Abrir chat">
        <svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
        {badge > 0 && <div className="ty-chatBadge">{badge}</div>}
      </button>

      {chatOpen && (
        <div className="ty-chatWin">
          <div className="ty-chatHeader">
            <div className="ty-chatAvatar">
              <svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
            </div>
            <div className="ty-chatHeaderInfo">
              <strong>Soporte al cliente</strong>
              <span>En línea</span>
            </div>
            <button className="ty-chatClose" onClick={toggleChat}>&times;</button>
          </div>
          <div className="ty-chatMsgs">
            {messages.map((m, i) => (
              <div key={i} className="ty-chatMsg">
                {m.text}
                <span className="ty-chatMsg__time">{m.time}</span>
              </div>
            ))}
            {typing && (
              <div className="ty-chatTyping"><i /><i /><i /></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
